/**
 * DeliveryVenues.jsx — Phase 2B
 * Admin management of exam venues (rooms/halls).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Building2, PlusCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDeliveryShell } from "../components/assessment-delivery/AssessmentDeliveryShell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch {
      msg = r.statusText;
    }
    throw Object.assign(new Error(msg || r.statusText), { status: r.status });
  }
  return r.json();
}

export default function DeliveryVenues() {
  const [venues, setVenues]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setCreate] = useState(false);
  const [form, setForm]         = useState({
    code: "", name: "", campus: "", building: "",
    room_number: "", capacity: "50", accessibility_notes: "",
  });
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await apiFetch("/delivery/venues");
      setVenues(list);
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Code and Name are required");
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/delivery/venues", {
        method: "POST",
        body: JSON.stringify({
          code: form.code.trim(),
          name: form.name.trim(),
          campus: form.campus.trim() || null,
          building: form.building.trim() || null,
          room_number: form.room_number.trim() || null,
          capacity: Number(form.capacity) || 50,
          accessibility_notes: form.accessibility_notes.trim() || null,
        }),
      });
      toast.success("Venue created");
      setCreate(false);
      setForm({ code: "", name: "", campus: "", building: "", room_number: "", capacity: "50", accessibility_notes: "" });
      load();
    } catch (e) {
      toast.error(`Create failed (${e.status || ""}): ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AssessmentDeliveryShell title="Venues" breadcrumb="Venues">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">
          {loading ? "Loading…" : `${venues.length} venue${venues.length !== 1 ? "s" : ""}`}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="venues-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400
                       border border-slate-700 bg-slate-900/60 hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setCreate(v => !v)}
            data-testid="create-venue-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       text-emerald-300 border border-emerald-700/60 bg-emerald-950/30
                       hover:bg-emerald-900/40 transition-colors"
          >
            <PlusCircle className="w-3.5 h-3.5" /> New Venue
          </button>
        </div>
      </div>

      {/* ── Create form ──────────────────────────────────────────────────── */}
      {showCreate && (
        <div className="mb-8 rounded-xl border border-emerald-800/40 bg-slate-900/60 p-6"
             data-testid="create-venue-form">
          <h2 className="text-base font-semibold text-slate-100 mb-4">New Venue</h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                placeholder="e.g. HALL-A"
                required
                data-testid="venue-form-code"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Main Examination Hall A"
                required
                data-testid="venue-form-name"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Campus</label>
              <input
                type="text"
                value={form.campus}
                onChange={e => setForm(f => ({ ...f, campus: e.target.value }))}
                placeholder="Optional"
                data-testid="venue-form-campus"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Capacity *</label>
              <input
                type="number"
                min="1"
                value={form.capacity}
                onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                required
                data-testid="venue-form-capacity"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Building</label>
              <input
                type="text"
                value={form.building}
                onChange={e => setForm(f => ({ ...f, building: e.target.value }))}
                placeholder="Optional"
                data-testid="venue-form-building"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Room Number</label>
              <input
                type="text"
                value={form.room_number}
                onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))}
                placeholder="Optional"
                data-testid="venue-form-room"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Accessibility Notes</label>
              <input
                type="text"
                value={form.accessibility_notes}
                onChange={e => setForm(f => ({ ...f, accessibility_notes: e.target.value }))}
                placeholder="Optional"
                data-testid="venue-form-accessibility"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="create-venue-submit"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-600
                           text-white transition-colors disabled:opacity-50"
              >
                {saving ? "Creating…" : "Create Venue"}
              </button>
              <button
                type="button"
                onClick={() => setCreate(false)}
                data-testid="cancel-venue-btn"
                className="px-4 py-2 rounded-lg text-sm text-slate-400 border border-slate-700
                           hover:border-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Venues list ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-slate-500 py-12 text-center" data-testid="venues-loading">
          Loading venues…
        </div>
      ) : venues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-12 text-center"
             data-testid="venues-empty">
          <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No venues configured.</p>
          <p className="text-slate-600 text-xs mt-1">Add a venue to enable seat allocation.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="venues-list">
          {venues.map(v => (
            <div
              key={v.id}
              className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4"
              data-testid={`venue-row-${v.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs font-mono text-slate-400 bg-slate-800/60 border border-slate-700/60
                                   rounded px-1.5 py-0.5">
                    {v.code}
                  </span>
                  {v.campus && <span className="text-xs text-slate-500">{v.campus}</span>}
                </div>
                <p className="text-sm font-medium text-slate-200">{v.name}</p>
                {(v.building || v.room_number) && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {[v.building, v.room_number].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-300">{v.capacity}</p>
                <p className="text-xs text-slate-500">capacity</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </AssessmentDeliveryShell>
  );
}
